import React, { useEffect, useState } from "react";
import { collection, onSnapshot, query, where, doc } from "firebase/firestore";
import { db } from "../Auth/firebase";
import {
  HiCheckCircle,
  HiExclamationCircle,
  HiCollection,
  HiCalendar,
} from "react-icons/hi";
import { useProjects } from "../../context/ProjectsContext";

function Stats({ userId }) {
  const [completedTasks, setCompletedTasks] = useState(0);
  const [incompleteTasks, setIncompleteTasks] = useState(0);
  const [totalProjects, setTotalProjects] = useState(0);
  const [upcomingEvents, setUpcomingEvents] = useState([]);
  const { activeProject } = useProjects();

  useEffect(() => {
    if (!activeProject) return;

    const fetchStats = () => {
      const projectId = activeProject.id;
      const projectRef = doc(db, "projects", projectId);
      const boardsCollection = collection(projectRef, "boards");

      const unsubscribeBoards = onSnapshot(
        boardsCollection,
        (boardsSnapshot) => {
          let completed = 0;
          let incomplete = 0;

          boardsSnapshot.forEach((boardDoc) => {
            const boardId = boardDoc.id;
            const tasksCollection = collection(
              db,
              `projects/${projectId}/boards/${boardId}/tasks`
            );

            onSnapshot(tasksCollection, (tasksSnapshot) => {
              tasksSnapshot.forEach((taskDoc) => {
                const taskData = taskDoc.data();
                taskData.completed ? completed++ : incomplete++;
              });

              setCompletedTasks(completed);
              setIncompleteTasks(incomplete);
            });
          });
        }
      );

      // Retrieve upcoming events for the active project
      const today = new Date().getTime();
      const eventsCollectionRef = collection(db, "events");
      const upcomingEventsQuery = query(
        eventsCollectionRef,
        where("day", ">=", today),
        where("projectId", "==", projectId)
      );

      const unsubscribeEvents = onSnapshot(
        upcomingEventsQuery,
        (eventsSnapshot) => {
          const eventsArray = eventsSnapshot.docs.map((doc) => ({
            id: doc.id,
            ...doc.data(),
          }));

          setUpcomingEvents(eventsArray);
        }
      );

      return () => {
        unsubscribeBoards();
        unsubscribeEvents();
      };
    };

    fetchStats();
  }, [userId, activeProject]);

  useEffect(() => {
    const fetchTotalProjects = () => {
      const projectsRef = collection(db, "projects");
      const projectsQuery = query(
        projectsRef,
        where("assignees", "array-contains", userId)
      );

      const unsubscribeProjects = onSnapshot(projectsQuery, (projectDocs) => {
        setTotalProjects(projectDocs.size);
      });

      return () => unsubscribeProjects();
    };

    fetchTotalProjects();
  }, [userId]);

  const totalTasks = completedTasks + incompleteTasks;
  const completionRate =
    totalTasks > 0 ? Math.round((completedTasks / totalTasks) * 100) : 0;

  return (
    <div className="flex gap-4 w-full">
      <Box>
        <div className="rounded-full flex items-center justify-center ">
          <HiCheckCircle fontSize="2.5rem" className="text-[#9999ff]" />
        </div>
        <div className="pl-4">
          <span className="text-lg text-gray-500">Completed Tasks</span>
          <div className="flex items-center">
            <strong className="text-3xl font-bold">{completedTasks}</strong>
            <span className="text-lg text-green-500 pl-3">
              {completionRate}%
            </span>
          </div>
        </div>
      </Box>
      <Box>
        <div>
          <HiExclamationCircle
            fontSize="2.5rem"
            className="text-[#ff8080] rounded-full flex items-center justify-center"
          />
        </div>
        <div className="pl-4">
          <span className="text-lg text-gray-500">Incomplete Tasks</span>
          <div className="flex items-center">
            <strong className="text-3xl font-bold">
              {incompleteTasks}/{totalTasks}
            </strong>
          </div>
        </div>
      </Box>
      <Box>
        <div>
          <HiCollection
            fontSize="2.5rem"
            className="text-[#9999ff] rounded-full flex items-center justify-center"
          />
        </div>
        <div className="pl-4">
          <span className="text-lg text-gray-500">Total Projects</span>
          <div className="flex items-center">
            <strong className="text-3xl font-bold">{totalProjects}</strong>
          </div>
        </div>
      </Box>
      <Box>
        <div>
          <HiCalendar
            fontSize="2.5rem"
            className="text-[#ff8080] rounded-full flex items-center justify-center"
          />
        </div>
        <div className="pl-4">
          <span className="text-lg text-gray-500">New Events</span>
          <div className="flex items-center">
            <strong className="text-3xl font-bold">
              {upcomingEvents.length}
            </strong>
          </div>
        </div>
      </Box>
    </div>
  );
}

export default Stats;

function Box({ children }) {
  return (
    <div className="bg-white rounded-lg mt-2 p-4 flex-1 border border-gray-200 flex items-center shadow-lg">
      {children}
    </div>
  );
}
